import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import {
  TpDataContext,
  TpDataTable,
  TpDataTableColumn,
  TpCanonicalTypeInfo,
  TpAnalyticsDataRefs,
  TpDataTableGroup,
  TpDerivedColumnPatch,
} from './interfaces';
import type { LedgerAction, MutationLedger } from './mutation-ledger/schemas';
import { analyticsDataStorage, updateCanonicalTypeRestrictionData } from './analyticsDataStorage';
import { TpUserEnSelectionAction } from './selection/userSelectionActions';
import {
  calculateEnSelectionFilter,
  createEmptyEnSelectionFilter,
  getEnSelectionFilterDef,
  notifyDataContextSelectionChanged,
  removeEnSelectionFilterStep,
} from './selection/selectionEnFilters';
import { cloneSelectionFilter, mergeSelectedItemsMap, mergeSelectionFilters } from './selection/selectionUtils';
import { applySnapshotToDataContext, TpSnapshotApplicationPlan } from './selection/snapshot';
import { getUserError } from '../components/miscUtils';
import { applyDerivedColumnPatches, validateDerivedColumnPatches } from './mutation-ledger/helpers';

const initialState: TpAnalyticsDataRefs = {
  dataContextRefs: {},
};

export function getDataContext(state: TpAnalyticsDataRefs, dataContextId: string): TpDataContext {
  return analyticsDataStorage.dataContexts[dataContextId]!;
}

export function getCanonicalTypeInfo(dataContext: TpDataContext, canonicalTypeId: string): TpCanonicalTypeInfo {
  return dataContext.canonicalTypes[canonicalTypeId]!;
}

export function getDendrogram(dataContext: TpDataContext, dendrogramId: string) {
  for (const dend of dataContext.dendrograms) if (dend.id == dendrogramId) return dend;
  throw getUserError(`Invalid dendrogram: ${dendrogramId}`);
}

export function getBitmap(dataContext: TpDataContext, bitmapId: string) {
  // Backwards compatibility: bitmaps were originally bound to the entity type
  const bitmap = dataContext.bitmaps[bitmapId] ?? getBitmapsForCanonicalType(dataContext, bitmapId)[0];

  if (!bitmap) {
    throw getUserError(`Invalid bitmap: ${bitmapId}`);
  }

  return bitmap;
}

export function getBitmapsForCanonicalType(dataContext: TpDataContext, canonicalTypeId: string) {
  return Object.values(dataContext.bitmaps).filter((bitmap) => bitmap.canonicalTypeId === canonicalTypeId);
}

export function getBitmapsForTag(dataContext: TpDataContext, bitmapTag: string) {
  return Object.values(dataContext.bitmaps).filter((bitmap) => bitmap.tags.includes(bitmapTag));
}

export function getColumnInfo(dataTable: TpDataTable, columnId: string): TpDataTableColumn {
  // @todo: potentially make faster via map
  for (const column of dataTable.columns) {
    if (column.id == columnId) return column;
  }
  throw getUserError(`Invalid column ${columnId} for datatable ${dataTable.id}`);
}

export function getGroupInfo(dataTable: TpDataTable, groupId: string): TpDataTableGroup {
  // @todo: potentially make faster via map
  for (const group of dataTable.columnGroups) {
    if (group.id == groupId) return group;
  }
  throw getUserError(`Invalid group ${groupId} for datatable ${dataTable.id}`);
}

export function dataTableHasItem(dataTable: TpDataTable, itemId: string) {
  return dataTable.itemIdMap[itemId] !== null && dataTable.itemIdMap[itemId] !== undefined;
}

function updateDataContextRef(state: TpAnalyticsDataRefs, dataContextId: string) {
  state.dataContextRefs[dataContextId]!.statusIndex++;
}

export interface CommitDashboardStateReplayPayload {
  dataContextId: string;
  dataTables: TpDataTable[];
  mutationLedger: MutationLedger;
  canonicalTypes: { [id: string]: TpCanonicalTypeInfo };
}

// Toggles a single item in/out of the current selection, reused both by the interactive reducer and by selection-snapshot replay
export function applyManualItemToggle(canonicalTypeInfo: TpCanonicalTypeInfo, itemId: string) {
  if (!canonicalTypeInfo.selectedItemsMap) {
    canonicalTypeInfo.selectedItemsMap = { [itemId]: true };
  } else {
    if (canonicalTypeInfo.selectedItemsMap[itemId]) delete canonicalTypeInfo.selectedItemsMap[itemId];
    else canonicalTypeInfo.selectedItemsMap[itemId] = true;
  }
  if (Object.keys(canonicalTypeInfo.selectedItemsMap).length == 0) {
    // No more items are selected => we are reverting to having no active selection
    canonicalTypeInfo.selectedItemsMap = null;
    canonicalTypeInfo.selectionFilter = createEmptyEnSelectionFilter();
  } else {
    canonicalTypeInfo.selectionFilter.manualSelection = true;
  }
}

export const analyticsDataSlice = createSlice({
  name: 'analyticsData',
  initialState,
  reducers: {
    setDataContext: (state, action: PayloadAction<string>) => {
      const dataContextId: string = action.payload;
      state.dataContextRefs[dataContextId] = {
        id: dataContextId,
        statusIndex: 0,
      };
    },
    removeDataContext: (state, action: PayloadAction<string>) => {
      if (state.dataContextRefs && action.payload in state.dataContextRefs) {
        delete state.dataContextRefs[action.payload];
        delete analyticsDataStorage.dataContexts[action.payload];
      }
    },

    performUserSelectionAction: (
      state,
      action: PayloadAction<{
        dataContextId: string;
        canonicalTypeId: string;
        userSelectionAction: TpUserEnSelectionAction;
      }>,
    ) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, action.payload.canonicalTypeId);
      getEnSelectionFilterDef(action.payload.userSelectionAction.type).handleUserAction(
        state,
        dataContext,
        canonicalTypeInfo,
        action.payload.userSelectionAction,
      );
      calculateEnSelectionFilter(dataContext, canonicalTypeInfo);
      updateDataContextRef(state, dataContext.id);
      notifyDataContextSelectionChanged(dataContext.id, canonicalTypeInfo.id);
    },

    removeSelectionStep: (
      state,
      action: PayloadAction<{
        dataContextId: string;
        canonicalTypeId: string;
        key: string;
      }>,
    ) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, action.payload.canonicalTypeId);
      removeEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, action.payload.key);
      calculateEnSelectionFilter(dataContext, canonicalTypeInfo);
      updateDataContextRef(state, dataContext.id);
      notifyDataContextSelectionChanged(dataContext.id, canonicalTypeInfo.id);
    },

    clearSelection: (state, action: PayloadAction<{ dataContextId: string; canonicalTypeId: string }>) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, action.payload.canonicalTypeId);
      canonicalTypeInfo.selectionFilter = createEmptyEnSelectionFilter();
      canonicalTypeInfo.selectedItemsMap = null;
      updateDataContextRef(state, dataContext.id);
      notifyDataContextSelectionChanged(dataContext.id, canonicalTypeInfo.id);
    },

    restrictSelection: (state, action: PayloadAction<{ dataContextId: string; canonicalTypeId: string }>) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, action.payload.canonicalTypeId);
      canonicalTypeInfo.restrictedItemsMap = mergeSelectedItemsMap(
        canonicalTypeInfo.selectedItemsMap,
        canonicalTypeInfo.restrictedItemsMap,
      );
      canonicalTypeInfo.restrictionFilter = mergeSelectionFilters(
        canonicalTypeInfo.restrictionFilter,
        cloneSelectionFilter(canonicalTypeInfo.selectionFilter),
      );
      canonicalTypeInfo.selectedItemsMap = null;
      canonicalTypeInfo.selectionFilter = createEmptyEnSelectionFilter();
      updateCanonicalTypeRestrictionData(canonicalTypeInfo);
      updateDataContextRef(state, dataContext.id);
      notifyDataContextSelectionChanged(dataContext.id, canonicalTypeInfo.id);
    },

    removeRestriction: (state, action: PayloadAction<{ dataContextId: string; canonicalTypeId: string }>) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, action.payload.canonicalTypeId);
      canonicalTypeInfo.selectionFilter = canonicalTypeInfo.restrictionFilter || createEmptyEnSelectionFilter();
      canonicalTypeInfo.selectedItemsMap = canonicalTypeInfo.restrictedItemsMap;
      canonicalTypeInfo.restrictionFilter = null;
      canonicalTypeInfo.restrictedItemsMap = null;
      canonicalTypeInfo.restrictedItemsMap = null;
      updateCanonicalTypeRestrictionData(canonicalTypeInfo);
      updateDataContextRef(state, dataContext.id);
      notifyDataContextSelectionChanged(dataContext.id, canonicalTypeInfo.id);
    },

    toggleSingleItemSelection: (
      state,
      action: PayloadAction<{ dataContextId: string; canonicalTypeId: string; itemId: string }>,
    ) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const canonicalTypeInfo = getCanonicalTypeInfo(dataContext, action.payload.canonicalTypeId);
      applyManualItemToggle(canonicalTypeInfo, action.payload.itemId);
      updateDataContextRef(state, dataContext.id);
      notifyDataContextSelectionChanged(dataContext.id, canonicalTypeInfo.id);
    },

    // Applies one or more derived-column patches (computed by action steps) to the data context in a single
    // dispatch. This is the sole choke point for creating/updating derived columns; a mutation-ledger entry
    upsertDerivedColumns: (
      state,
      action: PayloadAction<{ dataContextId: string; patches: TpDerivedColumnPatch[]; ledgerAction?: LedgerAction }>,
    ) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);

      validateDerivedColumnPatches(dataContext, action.payload.patches);
      applyDerivedColumnPatches(dataContext, action.payload.patches, action.payload.ledgerAction);
    },

    setInitialDataContextState: (state) => {
      state.dataContextRefs = {};
      analyticsDataStorage.dataContexts = {};
    },

    // Applies a full-data-context selection snapshot (see ./selection/snapshot/index.ts) onto the matching
    // canonical types of the target data context, in a single batched update: all mutations happen here, then
    // statusIndex is bumped once and change notifications are sent once per canonical type - so
    // subscribed components re-render exactly once per canonical type, not once per step/canonical type.
    pasteDataContextSelection: (
      state,
      action: PayloadAction<{ dataContextId: string; snapshot: TpSnapshotApplicationPlan }>,
    ) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      const snapshot = action.payload.snapshot;
      applySnapshotToDataContext(dataContext, snapshot);
      updateDataContextRef(state, dataContext.id);
      for (const canonicalTypeId of Object.keys(dataContext.canonicalTypes)) {
        notifyDataContextSelectionChanged(dataContext.id, canonicalTypeId);
      }
    },

    commitDashboardStateReplay: (state, action: PayloadAction<CommitDashboardStateReplayPayload>) => {
      const dataContext = getDataContext(state, action.payload.dataContextId);
      dataContext.dataTables = action.payload.dataTables;
      dataContext.mutationLedger = action.payload.mutationLedger;
      dataContext.canonicalTypes = action.payload.canonicalTypes;
      // Do not call augmentDataContext as it is called during replay (see applyDerivedColumnPatches)
      // on the new columns only: avoid re-augmenting the base columns and groups,
      // which would be redundant and potentially expensive for large datasets.
      // The replay engine is responsible for ensuring that the new columns are properly augmented.
      updateDataContextRef(state, dataContext.id);
      for (const canonicalTypeId of Object.keys(dataContext.canonicalTypes)) {
        notifyDataContextSelectionChanged(dataContext.id, canonicalTypeId);
      }
    },
  },
});

export const {
  setDataContext,
  removeDataContext,
  toggleSingleItemSelection,
  clearSelection,
  restrictSelection,
  removeRestriction,
  performUserSelectionAction,
  removeSelectionStep,
  upsertDerivedColumns,
  setInitialDataContextState,
  pasteDataContextSelection,
  commitDashboardStateReplay,
} = analyticsDataSlice.actions;

export const selectAnalyticsData = (state: RootState) => state.analyticsData;

export default analyticsDataSlice.reducer;
